import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export enum ToastType {
  success = 'success',
  error = 'error',
  warning = 'warning',
  indeterminate = 'indeterminate',
}

export interface ToastOptions {
  message: string | null;
  toastType?: ToastType;
  ariaLabel?: string | null;
  duration?: number;
  isVisible?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private isToastVisibleSubject = new BehaviorSubject<boolean>(false);
  isToastVisible$: Observable<boolean> =
    this.isToastVisibleSubject.asObservable();

  private messageSubject = new BehaviorSubject<string>('');
  message$ = this.messageSubject.asObservable();

  private toastTypeSubject = new BehaviorSubject<ToastType>(ToastType.warning);
  toastType$ = this.toastTypeSubject.asObservable();

  private ariaLabelSubject = new BehaviorSubject<string>('');
  ariaLabel$ = this.ariaLabelSubject.asObservable();

  private durationSubject = new BehaviorSubject<number>(4000);
  duration$ = this.durationSubject.asObservable();

  private autoCloseTimer: any = null;

  isToasterDisplayed: boolean = false;

  show(options: ToastOptions) {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    this.messageSubject.next(options.message || '');
    this.toastTypeSubject.next(options.toastType || ToastType.warning);
    this.ariaLabelSubject.next(options.ariaLabel || 'Toast notification');
    this.durationSubject.next(options.duration || 4000);

    this.isToastVisibleSubject.next(true);
    this.isToasterDisplayed = true;

    const duration = options.duration !== undefined ? options.duration : 4000;
    if (duration > 0) {
      this.autoCloseTimer = setTimeout(() => {
        this.close();
      }, duration);
    }
  }

  close() {
    this.isToastVisibleSubject.next(false);
    this.isToasterDisplayed = false;

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    setTimeout(() => {
      this.messageSubject.next('');
      this.toastTypeSubject.next(ToastType.warning);
      this.ariaLabelSubject.next('');
      this.durationSubject.next(4000);
    }, 300);
  }

  showSuccess(message: string, duration?: number) {
    this.show({
      message,
      toastType: ToastType.success,
      duration,
      ariaLabel: 'Success notification',
    });
  }

  showError(message: string, duration?: number) {
    this.show({
      message,
      toastType: ToastType.error,
      duration,
      ariaLabel: 'Error notification',
    });
  }

  showWarning(message: string, duration?: number) {
    this.show({
      message,
      toastType: ToastType.warning,
      duration,
      ariaLabel: 'Warning notification',
    });
  }

  showIndeterminate(message: string, duration?: number) {
    this.show({
      message,
      toastType: ToastType.indeterminate,
      duration,
      ariaLabel: 'Indeterminate notification',
    });
  }
}
